import React, { useState, useRef, useEffect } from 'react';
import { FiArrowDown, FiArrowUp, FiMove, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';

interface Item {
  id: string;
  name: string;
  description?: string;
  rate: number;
  is_taxable: boolean;
  use_customer_rate?: boolean;
  standard_rate?: number;
  customer_rate?: number | null;
  effective_rate?: number;
}

interface InvoiceItem {
  id?: string;
  item_id?: string;
  client_line_key?: string;
  line_order?: number;
  name?: string;
  description: string;
  qty: number;
  rate: number;
  amount: number;
  is_taxable?: boolean;
}

interface InvoiceItemsEditorProps {
  items: InvoiceItem[];
  availableItems: Item[];
  onItemsChange: (items: InvoiceItem[]) => void;
  onItemChange: (index: number, field: keyof InvoiceItem, value: any) => void;
  customerId?: string;
  customerTaxExempt?: boolean;
  getNumericItemFieldDisplayValue: (index: number, field: NumericItemField) => string;
  getNumericItemFieldKey: (index: number, field: NumericItemField) => string;
  onNumericItemDraftChange: (index: number, field: NumericItemField, rawValue: string) => void;
  onNumericItemFieldBlur: (index: number, field: NumericItemField) => void;
  itemNumericErrors: Record<string, string>;
  compactNumericHelpers?: boolean;
}

type NumericItemField = 'qty' | 'rate' | 'amount';

const InvoiceItemsEditor: React.FC<InvoiceItemsEditorProps> = ({
  items,
  availableItems,
  onItemsChange,
  onItemChange,
  customerId,
  customerTaxExempt = false,
  getNumericItemFieldDisplayValue,
  getNumericItemFieldKey,
  onNumericItemDraftChange,
  onNumericItemFieldBlur,
  itemNumericErrors,
  compactNumericHelpers = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(-1);
  const [focusedItemIndex, setFocusedItemIndex] = useState<number>(-1);
  const [rateLoading, setRateLoading] = useState(false);
  const [rateError, setRateError] = useState<string | null>(null);
  const [itemLabels, setItemLabels] = useState<string[]>([]);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);

  // Accessibility refs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const itemSelectorRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const filteredItems = availableItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Focus management for accessibility
  useEffect(() => {
    if (showItemSelector && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showItemSelector]);

  useEffect(() => {
    if (!showItemSelector) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!itemSelectorRef.current) return;
      if (itemSelectorRef.current.contains(event.target as Node)) return;
      setShowItemSelector(false);
      setSearchTerm('');
      setFocusedItemIndex(-1);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [showItemSelector]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showItemSelector) {
        setShowItemSelector(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showItemSelector]);

  // Handle keyboard navigation in item selector
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!showItemSelector) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedItemIndex(prev => 
            prev < filteredItems.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedItemIndex(prev => 
            prev > 0 ? prev - 1 : filteredItems.length - 1
          );
          break;
        case 'Enter':
          event.preventDefault();
          if (focusedItemIndex >= 0 && focusedItemIndex < filteredItems.length) {
            selectItemFromCatalog(filteredItems[focusedItemIndex], selectedItemIndex);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showItemSelector, filteredItems, focusedItemIndex, selectedItemIndex]);

  useEffect(() => {
    setItemLabels(
      items.map((item) => {
        const matchingItem = availableItems.find((candidate) => String(candidate.id) === String(item.item_id));
        return item.name || matchingItem?.name || '';
      })
    );
  }, [
    items.map((item) => `${String(item.id || '')}:${String(item.item_id || '')}:${String(item.name || '')}`).join('|'),
    availableItems.map((item) => `${String(item.id)}:${item.name}`).join('|')
  ]);

  const addItem = () => {
    const newItem: InvoiceItem = {
      description: '',
      qty: 1,
      rate: 0,
      amount: 0,
      is_taxable: false
    };
    onItemsChange([...items, newItem]);
  };

  const removeItem = (index: number) => {
    onItemsChange(items.filter((_, i) => i !== index));
  };

  const closeItemSelector = () => {
    setShowItemSelector(false);
    setSearchTerm('');
    setFocusedItemIndex(-1);
  };

  const moveItem = (fromIndex: number, toIndex: number) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= items.length ||
      toIndex >= items.length
    ) {
      return;
    }

    const nextItems = [...items];
    const [movedItem] = nextItems.splice(fromIndex, 1);
    nextItems.splice(toIndex, 0, movedItem);
    onItemsChange(nextItems);
    setSelectedItemIndex(-1);
    closeItemSelector();
  };

  const moveItemByOffset = (index: number, offset: number) => {
    moveItem(index, index + offset);
  };

  const handleDragStart = (event: React.DragEvent<HTMLButtonElement>, index: number) => {
    setDraggedItemIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>, index: number) => {
    event.preventDefault();
    const rawIndex = event.dataTransfer.getData('text/plain');
    const fromIndex = rawIndex === '' ? draggedItemIndex : Number(rawIndex);
    setDraggedItemIndex(null);

    if (typeof fromIndex !== 'number' || !Number.isInteger(fromIndex)) {
      return;
    }

    moveItem(fromIndex, index);
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
  };

  const selectItemFromCatalog = async (item: Item, index: number) => {
    onItemChange(index, 'item_id', item.id);
    onItemChange(index, 'name', item.name);
    onItemChange(index, 'is_taxable', customerTaxExempt ? false : item.is_taxable);
    setRateError(null);

    const effective = typeof item.effective_rate === 'number' ? item.effective_rate : item.rate;
    onItemChange(index, 'rate', effective);
    onItemChange(index, 'amount', effective); // Default to rate for qty=1

    // Display selected item name in the Item field (not persisted to backend)
    setItemLabels(prev => {
      const next = [...prev];
      next[index] = item.name;
      return next;
    });

    setShowItemSelector(false);
    setSearchTerm('');
    setFocusedItemIndex(-1);
  };

  const openItemSelector = (index: number) => {
    if (showItemSelector && selectedItemIndex === index) {
      setShowItemSelector(false);
      setSearchTerm('');
      setFocusedItemIndex(-1);
      return;
    }

    setSelectedItemIndex(index);
    setSearchTerm(itemLabels[index] || '');
    setShowItemSelector(true);
    setFocusedItemIndex(-1);
  };

  const displayRate = (it: Item) => {
    const r = typeof it.effective_rate === 'number' ? it.effective_rate : it.rate;
    return `$${Number(r || 0).toFixed(2)}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Line Items</h3>
        <button
          type="button"
          onClick={addItem}
          className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-200"
          aria-label="Add new line item"
        >
          <FiPlus className="mr-2 h-4 w-4" aria-hidden="true" />
          Add Item
        </button>
      </div>

      {/* Show message when no items are available */}
      {availableItems.length === 0 && (
        <div className="rounded-xl border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
          <div className="flex items-start gap-2">
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium">No items available</h3>
              <div className="mt-1 text-sm">
                <p>
                  You need to create some items first before you can add them to an invoice. 
                  Go to <a href="/items" className="underline font-medium">Items & Pricing</a> to create your first item.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {rateLoading && (
        <div className="text-sm text-blue-700 dark:text-blue-300">Fetching customer-specific rate...</div>
      )}
      {rateError && (
        <div className="text-sm text-red-700 dark:text-red-300">{rateError}</div>
      )}

      <div className="space-y-4" role="list" aria-label="Invoice line items">
        {items.map((item, index) => (
          <div 
            key={item.client_line_key || item.id || item.item_id || item.description || 'line-item'}
            className={`rounded-2xl border border-gray-200 bg-white p-5 elev-1 dark:bg-neutral-900 dark:border-neutral-800 ${
              draggedItemIndex === index ? 'opacity-60 ring-2 ring-sedra/40' : ''
            }`}
            role="listitem"
            aria-label={`Line item ${index + 1}`}
            onDragOver={handleDragOver}
            onDrop={(event) => handleDrop(event, index)}
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  draggable
                  onDragStart={(event) => handleDragStart(event, index)}
                  onDragEnd={handleDragEnd}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-500 cursor-grab hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra active:cursor-grabbing dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-300"
                  title="Drag to reorder"
                  aria-label={`Drag line item ${index + 1} to reorder`}
                >
                  <FiMove className="h-4 w-4" aria-hidden="true" />
                </button>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Line {index + 1}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => moveItemByOffset(index, -1)}
                  disabled={index === 0}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra disabled:cursor-not-allowed disabled:opacity-50 dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-300"
                  title="Move up"
                  aria-label={`Move line item ${index + 1} up`}
                >
                  <FiArrowUp className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => moveItemByOffset(index, 1)}
                  disabled={index === items.length - 1}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra disabled:cursor-not-allowed disabled:opacity-50 dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-300"
                  title="Move down"
                  aria-label={`Move line item ${index + 1} down`}
                >
                  <FiArrowDown className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div className="md:col-span-2">
                <label htmlFor={`item-name-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Item
                </label>
                <div ref={selectedItemIndex === index ? itemSelectorRef : null} className="relative mt-1">
                  <div className="flex min-w-0 gap-2">
                  <input
                    id={`item-name-${index}`}
                    type="text"
                    ref={selectedItemIndex === index ? searchInputRef : null}
                    value={showItemSelector && selectedItemIndex === index ? searchTerm : (itemLabels[index] || '')}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      setItemLabels(prev => {
                        const next = [...prev];
                        next[index] = nextValue;
                        return next;
                      });
                      onItemChange(index, 'name', nextValue);
                      setSelectedItemIndex(index);
                      setSearchTerm(nextValue);
                      setShowItemSelector(true);
                      setFocusedItemIndex(-1);
                    }}
                    onFocus={() => {
                      setSelectedItemIndex(index);
                    }}
                    className="min-w-0 flex-1 rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                    aria-describedby={`item-name-help-${index}`}
                    aria-expanded={showItemSelector && selectedItemIndex === index}
                    aria-controls={`item-name-options-${index}`}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    onClick={() => openItemSelector(index)}
                    className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-200"
                    title="Select from catalog"
                    aria-label={`Select item from catalog for line ${index + 1}`}
                    aria-expanded={showItemSelector && selectedItemIndex === index}
                    aria-controls={`item-name-options-${index}`}
                  >
                    <FiSearch className="h-4 w-4" aria-hidden="true" />
                  </button>
                  </div>

                  {showItemSelector && selectedItemIndex === index && (
                    <div
                      id={`item-name-options-${index}`}
                      className="absolute z-20 mt-2 w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg dark:border-neutral-800 dark:bg-neutral-900"
                      role="listbox"
                      aria-label={`Available items for line ${index + 1}`}
                    >
                      <div className="max-h-64 overflow-y-auto py-2">
                        {filteredItems.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {searchTerm ? 'No items found matching your search.' : 'No items available in your catalog.'}
                          </div>
                        ) : (
                          filteredItems.map((item, itemIndex) => (
                            <button
                              key={item.id}
                              type="button"
                              ref={(el) => { itemRefs.current[itemIndex] = el; }}
                              onClick={() => selectItemFromCatalog(item, index)}
                              className={`w-full px-4 py-3 text-left transition-colors hover:bg-sedra/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sedra ${
                                focusedItemIndex === itemIndex ? 'bg-sedra/10' : ''
                              }`}
                              role="option"
                              aria-selected={focusedItemIndex === itemIndex}
                              onMouseEnter={() => setFocusedItemIndex(itemIndex)}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-900 dark:text-white">{item.name}</div>
                                  {item.description && item.description !== item.name ? (
                                    <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{item.description}</div>
                                  ) : null}
                                </div>
                                <div className="shrink-0 text-right">
                                  <div className="font-medium text-gray-900 dark:text-white">{displayRate(item)}</div>
                                  {item.is_taxable ? (
                                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Taxable</div>
                                  ) : null}
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <p id={`item-name-help-${index}`} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Search and select an item, or type an item name.
                </p>

                {/* Line item description (persisted) */}
                <div className="mt-2">
                  <label htmlFor={`item-description-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    id={`item-description-${index}`}
                    value={item.description}
                    onChange={(e) => onItemChange(index, 'description', e.target.value)}
                    placeholder="Additional details about this line item..."
                    rows={2}
                    className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                    aria-describedby={`item-description-help-${index}`}
                  />
                  <p id={`item-description-help-${index}`} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Details shown on this line.
                  </p>
                </div>
              </div>
              <div>
                <label htmlFor={`item-qty-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Qty
                </label>
                <input
                  id={`item-qty-${index}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={getNumericItemFieldDisplayValue(index, 'qty')}
                  onChange={(e) => onNumericItemDraftChange(index, 'qty', e.target.value)}
                  onBlur={() => onNumericItemFieldBlur(index, 'qty')}
                  data-item-field-key={getNumericItemFieldKey(index, 'qty')}
                  className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                  aria-describedby={`item-qty-help-${index}`}
                />
                <p id={`item-qty-help-${index}`} className={compactNumericHelpers ? 'sr-only' : 'mt-1 text-xs text-gray-500 dark:text-gray-400'}>
                  Quantity
                </p>
                {itemNumericErrors[getNumericItemFieldKey(index, 'qty')] && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-300">{itemNumericErrors[getNumericItemFieldKey(index, 'qty')]}</p>
                )}
              </div>
              <div>
                <label htmlFor={`item-rate-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rate
                </label>
                <input
                  id={`item-rate-${index}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={getNumericItemFieldDisplayValue(index, 'rate')}
                  onChange={(e) => onNumericItemDraftChange(index, 'rate', e.target.value)}
                  onBlur={() => onNumericItemFieldBlur(index, 'rate')}
                  data-item-field-key={getNumericItemFieldKey(index, 'rate')}
                  className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                  aria-describedby={`item-rate-help-${index}`}
                />
                <p id={`item-rate-help-${index}`} className={compactNumericHelpers ? 'sr-only' : 'mt-1 text-xs text-gray-500 dark:text-gray-400'}>
                  Rate per unit
                </p>
                {itemNumericErrors[getNumericItemFieldKey(index, 'rate')] && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-300">{itemNumericErrors[getNumericItemFieldKey(index, 'rate')]}</p>
                )}
              </div>
              <div>
                <label htmlFor={`item-amount-${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Amount
                </label>
                <input
                  id={`item-amount-${index}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={getNumericItemFieldDisplayValue(index, 'amount')}
                  onChange={(e) => onNumericItemDraftChange(index, 'amount', e.target.value)}
                  onBlur={() => onNumericItemFieldBlur(index, 'amount')}
                  data-item-field-key={getNumericItemFieldKey(index, 'amount')}
                  className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                  aria-describedby={`item-amount-help-${index}`}
                />
                <p id={`item-amount-help-${index}`} className={compactNumericHelpers ? 'sr-only' : 'mt-1 text-xs text-gray-500 dark:text-gray-400'}>
                  Total amount for this item
                </p>
                {itemNumericErrors[getNumericItemFieldKey(index, 'amount')] && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-300">{itemNumericErrors[getNumericItemFieldKey(index, 'amount')]}</p>
                )}
              </div>
              <div className="flex items-end space-x-2">
                <label
                  className={`flex items-center ${customerTaxExempt ? 'cursor-not-allowed opacity-60' : ''}`}
                  title={customerTaxExempt ? 'Client is tax-exempt.' : undefined}
                >
                  <input
                    type="checkbox"
                    checked={customerTaxExempt ? false : (item.is_taxable || false)}
                    onChange={(e) => onItemChange(index, 'is_taxable', e.target.checked)}
                    disabled={customerTaxExempt}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                    aria-describedby={`item-taxable-help-${index}`}
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Taxable</span>
                </label>
                <p id={`item-taxable-help-${index}`} className="sr-only">
                  {customerTaxExempt ? 'Client is tax-exempt.' : 'Enable tax for this line item.'}
                </p>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="rounded-xl border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500 dark:bg-neutral-900 dark:border-red-700 dark:text-red-300"
                  title="Remove item"
                  aria-label={`Remove line item ${index + 1}`}
                >
                  <FiTrash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400" role="status">
          No items added yet. Click "Add Item" to get started.
        </div>
      )}
    </div>
  );
};

export default InvoiceItemsEditor; 
